import React, { useState, useCallback, useEffect, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "../components/Layout";
import FieldChangesTable from "../components/grants/recommendations/FieldChangesTable";
import { CannedReasonButtons } from "../components/grants/recommendations/CannedReasonButtons";
import { useToast } from "../contexts/ToastContext";
import { formatDateTime } from "../utils/date-formatters";
import { isSafeUrl, isValidUUID } from "../utils/url-validation";
import { RECOMMENDATION_NOTES } from "../constants/validation";
import { MUTATION_TIMEOUT_MS } from "../constants/api";
import { ACTION_BADGES } from "../constants/grants-labels";
import {
  getAdminRecommendationById,
  approveRecommendation,
  rejectRecommendation,
  revokeRecommendation,
  getNextPendingRecommendation,
  CallRecommendation,
  RecommendationOverrides,
} from "../services/grantsApi";
import { ArticleAttachmentList } from "../components/grants/shared/ArticleAttachmentList";
import ProjectCallsSection from "../components/grants/recommendations/ProjectCallsSection";
import RelatedRecommendationsSection from "../components/grants/recommendations/RelatedRecommendationsSection";
import ArticleInTargetCallStatus from "../components/grants/recommendations/ArticleInTargetCallStatus";
import OperatorOtherProjectsSection from "../components/grants/recommendations/OperatorOtherProjectsSection";
import SharedSourceOperatorsSection from "../components/grants/recommendations/SharedSourceOperatorsSection";
import {
  TIMEOUT_ERROR_MESSAGE,
  deriveApproveToast,
  getRecommendationErrorMessage,
} from "../components/grants/recommendations/recommendationReviewMessages";

/**
 * Returns Tailwind CSS color class for confidence score visualization.
 * @param confidence - Confidence score between 0 and 1
 * @returns Tailwind text color class: green (>=0.8), yellow (>=0.6), red (<0.6)
 */
const getConfidenceColor = (confidence: number): string => {
  if (confidence >= 0.8) return "text-green-600";
  if (confidence >= 0.6) return "text-yellow-600";
  return "text-red-600";
};

/**
 * Wraps a promise with a timeout mechanism.
 * Creates a race between the original promise and a timeout, rejecting with
 * a TimeoutError if the timeout expires first.
 * @param promise - The promise to wrap with timeout
 * @param timeoutMs - Maximum time to wait in milliseconds
 * @returns A promise that resolves with the original result or rejects on timeout
 * @throws Error with name "TimeoutError" if timeout expires
 */
const createTimeoutPromise = <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      const error = new Error(TIMEOUT_ERROR_MESSAGE);
      error.name = "TimeoutError";
      reject(error);
    }, timeoutMs);

    promise
      .then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        reject(err);
      });
  });
};

/**
 * External link icon component
 */
const ExternalLinkIcon: React.FC<{ className?: string }> = ({ className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

/**
 * Arrow left icon for back button
 */
const ArrowLeftIcon: React.FC<{ className?: string }> = ({ className = "h-5 w-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

/**
 * Arrow right icon for next recommendation button
 */
const ArrowRightIcon: React.FC<{ className?: string }> = ({ className = "h-5 w-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);

/**
 * Chevron icon for expandable section
 */
const ChevronIcon: React.FC<{ expanded: boolean; className?: string }> = ({ expanded, className = "h-5 w-5" }) => (
  <svg
    className={`${className} transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

/**
 * Page component for displaying detailed information about a single recommendation.
 *
 * ## Accessibility (a11y) Patterns
 *
 * ### Skip Link Pattern
 * A visually hidden link at the top of the page becomes visible on focus,
 * allowing keyboard users to bypass navigation and jump directly to action buttons.
 * - Element: `<a href="#actions">Przejdz do akcji</a>`
 * - CSS: `.sr-only` hidden by default, `.focus:not-sr-only` visible on focus
 * - Handler: `handleSkipToActions()` programmatically focuses the actions container
 *
 * ### ARIA Attributes Used
 * - `aria-label="Nawigacja breadcrumb"` - Identifies breadcrumb navigation for screen readers
 * - `aria-expanded={boolean}` - Indicates expandable article content state (true/false)
 * - `aria-controls="article-content"` - Links toggle button to controlled content region
 * - `aria-labelledby="section-heading"` - Associates section content with its heading
 * - `aria-hidden="true"` - Hides decorative icons from assistive technology
 * - `<section>` elements - Have implicit role="region" via aria-labelledby association
 *
 * ### Keyboard Shortcuts
 * - `ESC` (Escape): Two-tier behavior:
 *   1. If reject form is open: closes the form and clears input
 *   2. If no form open: navigates back to recommendations list
 * - `Enter`/`Space`: Toggles article content expansion when focused on toggle button
 *
 * ### Focus Management Approach
 * - **Reject form open**: Auto-focus on textarea via `useEffect` + `notesInputRef`
 * - **Article content expand**: Focus moves to content container (`articleContentRef`)
 * - **Article content collapse**: Focus returns to toggle button (`articleButtonRef`)
 * - **Skip link activation**: Focus moves to actions container (`actionsRef`)
 * - Implementation uses `pendingFocusRef` to defer focus until after state update renders
 *
 * @see docs/specifications/2026-01-29-recommendation-modal-context-links.md
 */
const GrantsRecommendationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  // State for reject form
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectNotes, setRejectNotes] = useState("");
  // State for revoke form
  const [showRevokeForm, setShowRevokeForm] = useState(false);
  const [revokeNotes, setRevokeNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  // State for editable field overrides
  const [fieldOverrides, setFieldOverrides] = useState<RecommendationOverrides>({});
  const [isFormDirty, setIsFormDirty] = useState(false);
  // AC-5: moderator override of update_existing -> create_new before approval
  const [forceCreateNew, setForceCreateNew] = useState(false);

  // State for unsaved changes confirmation modal
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const pendingNavigationRef = useRef<(() => void) | null>(null);

  // State for expandable article content
  const [isArticleContentExpanded, setIsArticleContentExpanded] = useState(false);
  const articleContentRef = useRef<HTMLDivElement>(null);
  const articleButtonRef = useRef<HTMLButtonElement>(null);
  const notesInputRef = useRef<HTMLTextAreaElement>(null);
  const revokeNotesInputRef = useRef<HTMLTextAreaElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);

  // Validate UUID format before making API call
  const isIdValid = isValidUUID(id);

  // Query recommendation details
  const { data: recommendation, isLoading, error: queryError, refetch } = useQuery<CallRecommendation>({
    queryKey: ["grants-recommendation-details", id],
    queryFn: () => getAdminRecommendationById(id!),
    enabled: !!id && isIdValid,
  });

  // Query next pending recommendation
  const { data: nextRecommendation, isLoading: isLoadingNext } = useQuery<CallRecommendation | null>({
    queryKey: ["grants-next-pending-recommendation", id],
    queryFn: () => getNextPendingRecommendation(id!),
    enabled: !!id && isIdValid,
    staleTime: 30000, // Cache for 30 seconds
  });

  // Approve mutation with timeout
  const approveMutation = useMutation({
    mutationFn: ({
      recId,
      overrides,
      forcedAction,
    }: {
      recId: string;
      overrides?: RecommendationOverrides;
      forcedAction?: "create_new";
    }) =>
      createTimeoutPromise(
        approveRecommendation(recId, undefined, overrides, forcedAction),
        MUTATION_TIMEOUT_MS,
      ),
    onSuccess: (data, variables) => {
      setIsFormDirty(false);
      queryClient.invalidateQueries({ queryKey: ["grants-admin-recommendations"] });
      // AC-4/AC-5: intent comes from the forcedAction actually sent with this
      // request (not the current override toggle), so the toast matches it.
      addToast(
        deriveApproveToast({
          suggestedAction: recommendation?.suggestedAction,
          forcedAction: variables.forcedAction,
          isNewCall: data.isNewCall,
        })
      );
      navigateToList();
    },
    onError: (err: unknown) => {
      setError(getRecommendationErrorMessage(err));
    },
  });

  // Reject mutation with timeout
  const rejectMutation = useMutation({
    mutationFn: ({ recId, notes }: { recId: string; notes: string }) =>
      createTimeoutPromise(rejectRecommendation(recId, notes), MUTATION_TIMEOUT_MS),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-admin-recommendations"] });
      addToast({ message: "Rekomendacja odrzucona", type: "success" });
      navigateToList();
    },
    onError: (err: unknown) => {
      setError(getRecommendationErrorMessage(err));
    },
  });

  // Revoke mutation with timeout
  const revokeMutation = useMutation({
    mutationFn: ({ recId, notes }: { recId: string; notes: string }) =>
      createTimeoutPromise(revokeRecommendation(recId, notes), MUTATION_TIMEOUT_MS),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-admin-recommendations"] });
      addToast({ message: "Cofnięto decyzję — rekomendacja wraca do kolejki", type: "success" });
      navigateToList();
    },
    onError: (err: unknown) => {
      setError(getRecommendationErrorMessage(err));
      refetch();
    },
  });

  const isPending = approveMutation.isPending || rejectMutation.isPending || revokeMutation.isPending;
  const isAlreadyReviewed = recommendation?.status !== "pending_review";
  const notesValid = rejectNotes.trim().length >= RECOMMENDATION_NOTES.MIN_LENGTH && rejectNotes.trim().length <= RECOMMENDATION_NOTES.MAX_LENGTH;
  const revokeNotesValid = revokeNotes.trim().length >= RECOMMENDATION_NOTES.MIN_LENGTH && revokeNotes.trim().length <= RECOMMENDATION_NOTES.MAX_LENGTH;

  // Build return URL with preserved query params
  const buildReturnUrl = useCallback(() => {
    const params = new URLSearchParams();
    const page = searchParams.get("page");
    const status = searchParams.get("status");
    if (page) params.set("page", page);
    if (status) params.set("status", status);
    const queryString = params.toString();
    return `/grants/admin/recommendations${queryString ? `?${queryString}` : ""}`;
  }, [searchParams]);

  const navigateToList = useCallback(() => {
    navigate(buildReturnUrl());
  }, [navigate, buildReturnUrl]);

  // Navigate to next pending recommendation
  const navigateToNext = useCallback(() => {
    if (nextRecommendation) {
      const params = new URLSearchParams();
      const page = searchParams.get("page");
      const status = searchParams.get("status");
      if (page) params.set("page", page);
      if (status) params.set("status", status);
      const queryString = params.toString();
      navigate(`/grants/recommendations/${nextRecommendation.id}${queryString ? `?${queryString}` : ""}`);
    }
  }, [nextRecommendation, navigate, searchParams]);

  // Handle approve - sends overrides if any fields were edited
  const handleApprove = useCallback(() => {
    if (!id) return;
    setError(null);
    const hasOverrides = Object.keys(fieldOverrides).length > 0;
    approveMutation.mutate({
      recId: id,
      overrides: hasOverrides ? fieldOverrides : undefined,
      forcedAction: forceCreateNew ? "create_new" : undefined,
    });
  }, [id, approveMutation, fieldOverrides, forceCreateNew]);

  // Handle reject
  const handleReject = useCallback(() => {
    if (!id) return;
    if (rejectNotes.trim().length < RECOMMENDATION_NOTES.MIN_LENGTH) {
      setError(`Powód odrzucenia musi mieć minimum ${RECOMMENDATION_NOTES.MIN_LENGTH} znaków.`);
      return;
    }
    if (rejectNotes.trim().length > RECOMMENDATION_NOTES.MAX_LENGTH) {
      setError(`Powód odrzucenia może mieć maksymalnie ${RECOMMENDATION_NOTES.MAX_LENGTH} znaków.`);
      return;
    }
    setError(null);
    rejectMutation.mutate({ recId: id, notes: rejectNotes.trim() });
  }, [id, rejectNotes, rejectMutation]);

  // Handle revoke
  const handleRevoke = useCallback(() => {
    if (!id) return;
    if (revokeNotes.trim().length < RECOMMENDATION_NOTES.MIN_LENGTH) {
      setError(`Powód cofnięcia musi mieć minimum ${RECOMMENDATION_NOTES.MIN_LENGTH} znaków.`);
      return;
    }
    if (revokeNotes.trim().length > RECOMMENDATION_NOTES.MAX_LENGTH) {
      setError(`Powód cofnięcia może mieć maksymalnie ${RECOMMENDATION_NOTES.MAX_LENGTH} znaków.`);
      return;
    }
    setError(null);
    revokeMutation.mutate({ recId: id, notes: revokeNotes.trim() });
  }, [id, revokeNotes, revokeMutation]);

  // Focus notes input when reject form opens
  useEffect(() => {
    if (showRejectForm && notesInputRef.current) {
      notesInputRef.current.focus();
    }
  }, [showRejectForm]);

  // Focus revoke notes input when revoke form opens
  useEffect(() => {
    if (showRevokeForm && revokeNotesInputRef.current) {
      revokeNotesInputRef.current.focus();
    }
  }, [showRevokeForm]);

  // Keyboard navigation: ESC to close reject form first, then go back
  // ESC is ignored when an input, select, or textarea has focus (to prevent accidental navigation)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isPending) {
        // Ignore ESC when a form input has focus
        const activeEl = document.activeElement;
        const isInputFocused = activeEl instanceof HTMLInputElement
          || activeEl instanceof HTMLSelectElement
          || activeEl instanceof HTMLTextAreaElement;

        if (isInputFocused) {
          // Blur the input instead of navigating
          (activeEl as HTMLElement).blur();
          return;
        }

        if (showRejectForm) {
          // First ESC: close the reject form
          setShowRejectForm(false);
          setRejectNotes("");
          setError(null);
        } else if (showRevokeForm) {
          // First ESC: close the revoke form
          setShowRevokeForm(false);
          setRevokeNotes("");
          setError(null);
        } else if (isFormDirty) {
          // Has unsaved changes: show confirmation modal
          pendingNavigationRef.current = navigateToList;
          setShowUnsavedModal(true);
        } else {
          // No form open and no unsaved changes: navigate back
          navigateToList();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [navigateToList, isPending, showRejectForm, showRevokeForm, isFormDirty]);

  // Ref to track pending focus target after render
  const pendingFocusRef = useRef<'content' | 'button' | null>(null);

  // Toggle article content expansion with focus management
  const toggleArticleContent = useCallback(() => {
    setIsArticleContentExpanded((prev) => {
      const newState = !prev;
      // Set pending focus target to be applied after render
      pendingFocusRef.current = newState ? 'content' : 'button';
      return newState;
    });
  }, []);

  // Apply focus after article content state changes
  useEffect(() => {
    if (pendingFocusRef.current === 'content' && articleContentRef.current) {
      articleContentRef.current.focus();
      pendingFocusRef.current = null;
    } else if (pendingFocusRef.current === 'button' && articleButtonRef.current) {
      articleButtonRef.current.focus();
      pendingFocusRef.current = null;
    }
  }, [isArticleContentExpanded]);

  // Navigation guard: browser beforeunload event when form is dirty
  useEffect(() => {
    if (!isFormDirty) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isFormDirty]);

  // Handle field value changes from FieldChangesTable
  const handleValuesChange = useCallback(
    (overrides: RecommendationOverrides, isDirty: boolean) => {
      setFieldOverrides(overrides);
      setIsFormDirty(isDirty);
    },
    []
  );

  // AC-5: handle moderator toggling the update_existing -> create_new override
  const handleActionOverrideChange = useCallback((forceCreateNewValue: boolean) => {
    setForceCreateNew(forceCreateNewValue);
  }, []);

  // Handle navigation with unsaved changes guard
  const handleNavigateWithGuard = useCallback(
    (navigationFn: () => void) => {
      if (isFormDirty) {
        pendingNavigationRef.current = navigationFn;
        setShowUnsavedModal(true);
      } else {
        navigationFn();
      }
    },
    [isFormDirty]
  );

  // Confirm leaving with unsaved changes
  const handleConfirmLeave = useCallback(() => {
    setShowUnsavedModal(false);
    setIsFormDirty(false);
    if (pendingNavigationRef.current) {
      pendingNavigationRef.current();
      pendingNavigationRef.current = null;
    }
  }, []);

  // Cancel leaving
  const handleCancelLeave = useCallback(() => {
    setShowUnsavedModal(false);
    pendingNavigationRef.current = null;
  }, []);

  // Handle skip link to actions
  const handleSkipToActions = useCallback(() => {
    actionsRef.current?.focus();
  }, []);

  // Extract context from extractedDataSnapshot.projectId (primary) or sourceLinks (fallback)
  // Use extractedDataSnapshot.projectId to find the correct sourceLink, as articles may be linked to multiple projects
  const getContext = useCallback(() => {
    if (!recommendation?.article?.sourceLinks?.length) {
      return { source: null, project: null, operator: null, competition: null, isManualSource: true };
    }

    const sourceLinks = recommendation.article.sourceLinks;
    const expectedProjectId = recommendation.extractedDataSnapshot?.projectId;

    // Try to find the sourceLink matching the project from analysis
    let matchedLink = sourceLinks[0]; // fallback to first
    if (expectedProjectId) {
      const found = sourceLinks.find(
        (link) => link.source?.project?.id === expectedProjectId
      );
      if (found) {
        matchedLink = found;
      }
    }

    const source = matchedLink.source;
    const project = source?.project || null;
    const operator = project?.operator || null;
    const competition = project?.competition || null;
    return { source, project, operator, competition, isManualSource: false };
  }, [recommendation]);

  const context = recommendation ? getContext() : { source: null, project: null, operator: null, competition: null, isManualSource: true };

  // Loading state
  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="animate-pulse">
            <div className="h-4 bg-neutral-200 rounded w-48 mb-4"></div>
            <div className="h-8 bg-neutral-200 rounded w-96 mb-2"></div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-6 bg-neutral-200 rounded w-32"></div>
              <div className="h-4 bg-neutral-200 rounded w-full"></div>
              <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
              <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Invalid UUID format - show error immediately without API call
  if (!isIdValid) {
    return (
      <Layout>
        <div className="space-y-6">
          <nav className="text-sm text-neutral-500" aria-label="Nawigacja breadcrumb">
            <Link to="/grants/admin/recommendations" className="hover:text-primary-600">
              Rekomendacje
            </Link>
            <span className="mx-2">&gt;</span>
            <span>Błąd</span>
          </nav>

          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 text-center">
            <div className="text-red-600 mb-4">
              <svg className="h-12 w-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="font-medium">Nieprawidłowy identyfikator rekomendacji</p>
              <p className="text-sm mt-1">Podany identyfikator ma nieprawidłowy format.</p>
            </div>
            <Link
              to={buildReturnUrl()}
              className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
            >
              Wróć do listy
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  // Error or 404 state
  if (queryError || !recommendation) {
    return (
      <Layout>
        <div className="space-y-6">
          <nav className="text-sm text-neutral-500" aria-label="Nawigacja breadcrumb">
            <Link to="/grants/admin/recommendations" className="hover:text-primary-600">
              Rekomendacje
            </Link>
            <span className="mx-2">&gt;</span>
            <span>Błąd</span>
          </nav>

          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 text-center">
            <div className="text-red-600 mb-4">
              <svg className="h-12 w-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="font-medium">Nie udało się pobrać danych rekomendacji</p>
              <p className="text-sm mt-1">
                {queryError instanceof Error ? queryError.message : "Rekomendacja nie została znaleziona"}
              </p>
            </div>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => refetch()}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                Spróbuj ponownie
              </button>
              <Link
                to={buildReturnUrl()}
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
              >
                Wróć do listy
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const actionBadge = ACTION_BADGES[recommendation.suggestedAction] || {
    label: recommendation.suggestedAction,
    breadcrumbLabel: recommendation.suggestedAction,
    className: "bg-neutral-100 text-neutral-800",
  };

  const shortId = recommendation.id.substring(0, 8);

  return (
    <Layout>
      {/* Skip link for accessibility */}
      <a
        href="#actions"
        onClick={(e) => { e.preventDefault(); handleSkipToActions(); }}
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-md"
      >
        Przejdź do akcji
      </a>

      <div className="space-y-6">
        {/* Back button and breadcrumbs */}
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => handleNavigateWithGuard(navigateToList)}
            className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Powrót do listy
          </button>
          <nav className="text-sm text-neutral-500" aria-label="Nawigacja breadcrumb">
            <Link to="/grants/admin/recommendations" className="hover:text-primary-600">
              Rekomendacje
            </Link>
            <span className="mx-2">&gt;</span>
            <span className="text-neutral-900">{actionBadge.breadcrumbLabel} #{shortId}</span>
          </nav>
        </div>

        {/* Sticky action header */}
        <div
          ref={actionsRef}
          id="actions"
          tabIndex={-1}
          className="sticky top-0 z-10 bg-white border border-neutral-200 rounded-lg shadow-sm p-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${actionBadge.className}`}>
                {actionBadge.label}
              </span>
              <span className={`text-lg font-semibold ${getConfidenceColor(recommendation.confidence)}`} title="Pewność LLM że rekomendacja jest poprawna">
                {(recommendation.confidence * 100).toFixed(0)}%
              </span>
            </div>

            <div className="flex items-center gap-3">
              {!isAlreadyReviewed ? (
                <>
                  {showRejectForm ? (
                    <>
                      <button
                        onClick={() => setShowRejectForm(false)}
                        disabled={isPending}
                        className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50"
                      >
                        Anuluj
                      </button>
                      <button
                        onClick={handleReject}
                        disabled={isPending || !notesValid}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                      >
                        {rejectMutation.isPending && (
                          <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                        )}
                        Odrzuć
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setShowRejectForm(true)}
                        disabled={isPending}
                        className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50"
                      >
                        Odrzuć
                      </button>
                      <button
                        onClick={handleApprove}
                        disabled={isPending}
                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                      >
                        {approveMutation.isPending && (
                          <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                        )}
                        Zatwierdź
                      </button>
                    </>
                  )}
                </>
              ) : (
                <>
                  {showRevokeForm ? (
                    <>
                      <button
                        onClick={() => { setShowRevokeForm(false); setRevokeNotes(""); setError(null); }}
                        disabled={isPending}
                        className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50"
                      >
                        Anuluj
                      </button>
                      <button
                        onClick={handleRevoke}
                        disabled={isPending || !revokeNotesValid}
                        className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                      >
                        {revokeMutation.isPending && (
                          <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                        )}
                        Cofnij decyzję
                      </button>
                    </>
                  ) : (
                    <>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        recommendation.status === "approved"
                          ? "bg-green-100 text-green-800"
                          : recommendation.status === "rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-blue-100 text-blue-800"
                      }`}>
                        {recommendation.status === "approved"
                          ? "Zatwierdzona"
                          : recommendation.status === "rejected"
                          ? "Odrzucona"
                          : "Automatycznie zatwierdzona"}
                      </span>
                      {recommendation.status === "rejected" && (
                        <button
                          onClick={() => setShowRevokeForm(true)}
                          disabled={isPending}
                          className="px-4 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 disabled:opacity-50"
                        >
                          Cofnij decyzję
                        </button>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Separator when not in reject/revoke form */}
              {!showRejectForm && !showRevokeForm && (
                <div className="h-6 w-px bg-neutral-300"></div>
              )}

              {/* Next recommendation button */}
              {!showRejectForm && !showRevokeForm && (
                <>
                  {isLoadingNext ? (
                    <span className="px-4 py-2 text-sm text-neutral-500 inline-flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-neutral-400"></span>
                      Sprawdzanie...
                    </span>
                  ) : nextRecommendation ? (
                    <button
                      onClick={() => handleNavigateWithGuard(navigateToNext)}
                      disabled={isPending}
                      className="px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 disabled:opacity-50 inline-flex items-center gap-2"
                    >
                      Następna rekomendacja
                      <ArrowRightIcon className="h-4 w-4" />
                    </button>
                  ) : (
                    <span className="px-4 py-2 text-sm text-neutral-500">Brak kolejnych</span>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Reject form in sticky header */}
          {showRejectForm && !isAlreadyReviewed && (
            <div className="mt-4 pt-4 border-t border-neutral-200">
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Powód odrzucenia <span className="text-red-500">*</span>
              </label>
              <textarea
                ref={notesInputRef}
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                placeholder={`Podaj powód odrzucenia (${RECOMMENDATION_NOTES.MIN_LENGTH}-${RECOMMENDATION_NOTES.MAX_LENGTH} znaków)`}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                rows={2}
                disabled={isPending}
              />
              <CannedReasonButtons
                value={rejectNotes}
                onAppend={setRejectNotes}
                maxLength={RECOMMENDATION_NOTES.MAX_LENGTH}
                disabled={isPending}
                textareaRef={notesInputRef}
              />
              <span className={`text-xs ${rejectNotes.length < RECOMMENDATION_NOTES.MIN_LENGTH ? "text-red-500" : rejectNotes.length > RECOMMENDATION_NOTES.MAX_LENGTH ? "text-red-500" : "text-neutral-500"}`}>
                {rejectNotes.length < RECOMMENDATION_NOTES.MIN_LENGTH
                  ? `Minimum ${RECOMMENDATION_NOTES.MIN_LENGTH} znaków (brakuje ${RECOMMENDATION_NOTES.MIN_LENGTH - rejectNotes.length})`
                  : rejectNotes.length > RECOMMENDATION_NOTES.MAX_LENGTH
                  ? `Przekroczono limit (${rejectNotes.length - RECOMMENDATION_NOTES.MAX_LENGTH} za dużo)`
                  : `${rejectNotes.length}/${RECOMMENDATION_NOTES.MAX_LENGTH} znaków`}
              </span>
            </div>
          )}

          {/* Revoke form in sticky header */}
          {showRevokeForm && recommendation.status === "rejected" && (
            <div className="mt-4 pt-4 border-t border-neutral-200">
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Powód cofnięcia decyzji <span className="text-red-500">*</span>
              </label>
              <textarea
                ref={revokeNotesInputRef}
                value={revokeNotes}
                onChange={(e) => setRevokeNotes(e.target.value)}
                placeholder={`Podaj powód cofnięcia (${RECOMMENDATION_NOTES.MIN_LENGTH}-${RECOMMENDATION_NOTES.MAX_LENGTH} znaków)`}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                rows={2}
                disabled={isPending}
              />
              <span className={`text-xs ${revokeNotes.length < RECOMMENDATION_NOTES.MIN_LENGTH ? "text-red-500" : revokeNotes.length > RECOMMENDATION_NOTES.MAX_LENGTH ? "text-red-500" : "text-neutral-500"}`}>
                {revokeNotes.length < RECOMMENDATION_NOTES.MIN_LENGTH
                  ? `Minimum ${RECOMMENDATION_NOTES.MIN_LENGTH} znaków (brakuje ${RECOMMENDATION_NOTES.MIN_LENGTH - revokeNotes.length})`
                  : revokeNotes.length > RECOMMENDATION_NOTES.MAX_LENGTH
                  ? `Przekroczono limit (${revokeNotes.length - RECOMMENDATION_NOTES.MAX_LENGTH} za dużo)`
                  : `${revokeNotes.length}/${RECOMMENDATION_NOTES.MAX_LENGTH} znaków`}
              </span>
            </div>
          )}

          {/* Error message with retry option for timeout */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center justify-between">
              <p className="text-sm text-red-700">{error}</p>
              {error === TIMEOUT_ERROR_MESSAGE && (
                <button
                  onClick={() => {
                    setError(null);
                    // Retry the last action - if we were approving, re-approve; if rejecting, re-reject
                    if (approveMutation.variables?.recId) {
                      approveMutation.mutate(approveMutation.variables);
                    } else if (rejectMutation.variables) {
                      rejectMutation.mutate(rejectMutation.variables);
                    }
                  }}
                  disabled={isPending}
                  className="ml-4 px-3 py-1 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50"
                >
                  Spróbuj ponownie
                </button>
              )}
            </div>
          )}
        </div>

        {/* Two-column layout: Context (left) + Extracted data (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left column: Context + Existing calls + Related recommendations */}
          <div className="space-y-6">
            {/* Context section */}
            <section aria-labelledby="context-heading" className="bg-white rounded-lg shadow-sm border border-neutral-200">
              <div className="px-6 py-4 border-b border-neutral-200">
                <h2 id="context-heading" className="text-lg font-medium text-neutral-900 text-left">Kontekst</h2>
              </div>
              <div className="px-6 py-4">
                <dl className="grid grid-cols-1 gap-y-4">
                  {/* Project */}
                  {context.project && (
                    <div>
                      <dt className="text-sm font-medium text-neutral-500 text-left">Projekt</dt>
                      <dd className="mt-1 text-sm text-left">
                        <Link to={`/grants/projects/${context.project.id}`} className="text-primary-600 hover:text-primary-800">
                          {context.project.name}
                        </Link>
                      </dd>
                    </div>
                  )}

                  {/* Operator */}
                  {context.operator && (
                    <div>
                      <dt className="text-sm font-medium text-neutral-500 text-left">Operator</dt>
                      <dd className="mt-1 text-sm text-left">
                        <Link to={`/grants/operators/${context.operator.id}`} className="text-primary-600 hover:text-primary-800">
                          {context.operator.name}
                        </Link>
                      </dd>
                    </div>
                  )}

                  {/* Competition */}
                  {context.competition && (
                    <div>
                      <dt className="text-sm font-medium text-neutral-500 text-left">Konkurs</dt>
                      <dd className="mt-1 text-sm text-left">
                        <Link to={`/grants/competitions/${context.competition.id}`} className="text-primary-600 hover:text-primary-800">
                          {context.competition.name}
                        </Link>
                      </dd>
                    </div>
                  )}

                  {/* Source */}
                  <div>
                    <dt className="text-sm font-medium text-neutral-500 text-left">Źródło</dt>
                    <dd className="mt-1 text-sm text-left">
                      {context.isManualSource ? (
                        <span className="text-neutral-700">Dodane ręcznie</span>
                      ) : context.source?.url && isSafeUrl(context.source.url) ? (
                        <a
                          href={context.source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800"
                          aria-label={`${context.source.name} - otwiera w nowej karcie`}
                        >
                          {context.source.name}
                          <ExternalLinkIcon className="h-3.5 w-3.5" />
                        </a>
                      ) : (
                        <span className="text-neutral-700">{context.source?.name || "Nieznane"}</span>
                      )}
                    </dd>
                  </div>

                  {/* Article */}
                  <div>
                    <dt className="text-sm font-medium text-neutral-500 text-left">Artykuł</dt>
                    <dd className="mt-1 text-sm text-left">
                      {recommendation.article?.url && isSafeUrl(recommendation.article.url) ? (
                        <a
                          href={recommendation.article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800"
                          aria-label={`${recommendation.article.title} - otwiera w nowej karcie`}
                        >
                          <span className="truncate max-w-xs">{recommendation.article.title}</span>
                          <ExternalLinkIcon className="h-3.5 w-3.5 flex-shrink-0" />
                        </a>
                      ) : (
                        <span className="text-neutral-700">{recommendation.article?.title || "Brak artykułu"}</span>
                      )}
                    </dd>
                  </div>

                  {/* Article-in-target-call status (UPDATE/LINK only) — tells the
                      moderator up-front whether this article is already attached
                      to the call being updated, and when, so they don't have to
                      open the call and compare articles by hand. Gated on the
                      recommendation TYPE (not on the optional link payload) so the
                      section always renders for aktualizacja/powiązanie and never
                      for create_new — independent of whether the backend supplied
                      targetCallArticleLink (missing/null falls back gracefully). */}
                  {recommendation.suggestedAction !== "create_new" && (
                    <div>
                      <dt className="text-sm font-medium text-neutral-500 text-left">
                        Czy artykuł jest już w naborze?
                      </dt>
                      <dd className="mt-1 text-sm text-left">
                        {recommendation.targetCallArticleLink ? (
                          <ArticleInTargetCallStatus
                            link={recommendation.targetCallArticleLink}
                          />
                        ) : (
                          <span className="text-neutral-700">
                            Brak danych o powiązaniu
                          </span>
                        )}
                      </dd>
                    </div>
                  )}
                </dl>

                {/* Attachments — rendered outside the <dl> above: ArticleAttachmentList's
                    root is a <div role="group">, which would break the <dt>/<dd> content
                    model if inserted directly between them. Renders nothing when the
                    article has no (safe) attachments or no article is linked. */}
                <ArticleAttachmentList
                  attachments={recommendation.article?.attachments ?? []}
                  articleTitle={recommendation.article?.title}
                  className="mt-4"
                  headingClassName="text-sm font-medium text-neutral-500 text-left"
                />
              </div>
            </section>

            {/* Project calls section - show existing calls in the project */}
            {context.project && (
              <ProjectCallsSection
                projectId={context.project.id}
                targetCallId={recommendation.targetCallId}
              />
            )}

            {/* Related recommendations section - show other recommendations in the project */}
            {context.project && id && (
              <RelatedRecommendationsSection
                projectId={context.project.id}
                currentRecommendationId={id}
                initialCount={recommendation.relatedRecommendationsCount}
              />
            )}
          </div>

          {/* Right column: Extracted data / New call data */}
          <div className="space-y-6">
            {/* Target call deleted warning */}
            {recommendation.suggestedAction !== "create_new" && !recommendation.targetCall && (
              <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Uwaga:</strong> Docelowy nabór został usunięty.
                </p>
              </div>
            )}

            {/* Extracted data section */}
            <section aria-labelledby="extracted-data-heading" className="bg-white rounded-lg shadow-sm border border-neutral-200">
              <div className="px-6 py-4 border-b border-neutral-200">
                <h2 id="extracted-data-heading" className="text-lg font-medium text-neutral-900 text-left">
                  {recommendation.suggestedAction === "create_new" ? "Dane nowego naboru" : "Porównanie zmian"}
                </h2>
              </div>
              <div className="px-6 py-4">
                {recommendation.extractedDataSnapshot ? (
                  <FieldChangesTable
                    action={recommendation.suggestedAction}
                    extractedData={recommendation.extractedDataSnapshot}
                    targetCall={recommendation.targetCall}
                    editable={!isAlreadyReviewed}
                    onValuesChange={handleValuesChange}
                    onActionOverrideChange={handleActionOverrideChange}
                  />
                ) : (
                  <p className="text-sm text-neutral-500 italic">Brak danych ekstrakcji</p>
                )}
              </div>
            </section>
          </div>
        </div>

        {/* Full-width sections below the two columns */}

        {/* Other operator projects section - show other projects of same operator */}
        {context.operator && (
          <OperatorOtherProjectsSection
            operatorId={context.operator.id}
            currentProjectId={context.project?.id}
          />
        )}

        {context.operator && (
          <SharedSourceOperatorsSection
            operatorId={context.operator.id}
          />
        )}

        {/* Analysis history section */}
        <section aria-labelledby="analysis-history-heading" className="bg-white rounded-lg shadow-sm border border-neutral-200">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h2 id="analysis-history-heading" className="text-lg font-medium text-neutral-900 text-left">Historia analizy</h2>
          </div>
          <div className="px-6 py-4">
            <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4">
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Odkryto artykuł</dt>
                <dd className="mt-1 text-sm text-neutral-900 text-left">
                  {recommendation.article?.createdAt
                    ? formatDateTime(recommendation.article.createdAt)
                    : "-"}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Data analizy</dt>
                <dd className="mt-1 text-sm text-neutral-900 text-left">
                  {formatDateTime(recommendation.createdAt)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Analiza</dt>
                <dd className="mt-1 text-sm text-left">
                  {recommendation.analysisId ? (
                    <span className="text-neutral-700 font-mono text-xs">{recommendation.analysisId.substring(0, 8)}</span>
                  ) : (
                    <span className="text-neutral-500 italic">Brak danych (legacy)</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left" title="Pewność LLM że rekomendacja jest poprawna">
                  Pewność <span className="text-neutral-400 cursor-help">(?)</span>
                </dt>
                <dd className={`mt-1 text-sm font-semibold ${getConfidenceColor(recommendation.confidence)}`}>
                  {(recommendation.confidence * 100).toFixed(0)}%
                </dd>
              </div>
              {recommendation.reason && (
                <div className="md:col-span-2 lg:col-span-4">
                  <dt className="text-sm font-medium text-neutral-500 text-left">Uzasadnienie</dt>
                  <dd className="mt-1 text-sm text-neutral-900 text-left break-words">{recommendation.reason}</dd>
                </div>
              )}
            </dl>
          </div>
        </section>

        {/* Article content section (expandable) */}
        <section aria-labelledby="article-content-heading" className="bg-white rounded-lg shadow-sm border border-neutral-200">
          <div className="px-6 py-4">
            {recommendation.article?.rawContent ? (
              <>
                <button
                  ref={articleButtonRef}
                  onClick={toggleArticleContent}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleArticleContent();
                    }
                  }}
                  className="flex items-center justify-between w-full text-left"
                  aria-expanded={isArticleContentExpanded}
                  aria-controls="article-content"
                >
                  <h2 id="article-content-heading" className="text-lg font-medium text-neutral-900">Treść artykułu</h2>
                  <ChevronIcon expanded={isArticleContentExpanded} />
                </button>

                <div
                  ref={articleContentRef}
                  id="article-content"
                  tabIndex={-1}
                  className={`mt-4 pt-4 border-t border-neutral-200 ${isArticleContentExpanded ? "" : "hidden"}`}
                >
                  <div className="max-h-[300px] md:max-h-[500px] lg:max-h-[700px] overflow-y-auto p-4 bg-neutral-50 rounded-md border border-neutral-200">
                    <pre className="whitespace-pre-wrap text-sm text-neutral-700 font-sans">
                      {recommendation.article.rawContent}
                    </pre>
                  </div>
                  {recommendation.article.url && isSafeUrl(recommendation.article.url) && (
                    <div className="mt-4">
                      <a
                        href={recommendation.article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800"
                        aria-label={`Otwórz oryginalny artykuł: ${recommendation.article.title} - otwiera w nowej karcie`}
                      >
                        Otwórz oryginalny artykuł
                        <ExternalLinkIcon className="h-4 w-4" />
                      </a>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-between">
                <h2 id="article-content-heading" className="text-lg font-medium text-neutral-900">Treść artykułu</h2>
                <span className="text-sm text-neutral-500 italic">Treść niedostępna</span>
              </div>
            )}
          </div>
        </section>

        {/* Review notes (if already reviewed) */}
        {isAlreadyReviewed && recommendation.reviewNotes && (
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
            <div className="px-6 py-4 border-b border-neutral-200">
              <h2 className="text-lg font-medium text-neutral-900 text-left">Notatka recenzenta</h2>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-neutral-700">{recommendation.reviewNotes}</p>
              {recommendation.reviewedAt && (
                <p className="text-xs text-neutral-500 mt-2">
                  {formatDateTime(recommendation.reviewedAt)}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Unsaved changes confirmation modal */}
      {showUnsavedModal && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="unsaved-changes-title"
          aria-modal="true"
          role="dialog"
        >
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={handleCancelLeave}
            aria-hidden="true"
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-sm transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
              <div className="px-6 py-4">
                <h3
                  id="unsaved-changes-title"
                  className="text-lg font-semibold text-neutral-900 mb-2"
                >
                  Niezapisane zmiany
                </h3>
                <p className="text-sm text-neutral-600">
                  Masz niezapisane zmiany w polach rekomendacji. Czy na pewno chcesz opuścić stronę?
                </p>
              </div>
              <div className="flex justify-end gap-3 border-t border-neutral-200 px-6 py-4">
                <button
                  type="button"
                  onClick={handleCancelLeave}
                  className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
                >
                  Zostań
                </button>
                <button
                  type="button"
                  onClick={handleConfirmLeave}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  Opuść stronę
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default GrantsRecommendationDetails;
